/**
 * Mission log — a short list of things to do in xiaoOS, ticked off as the
 * visitor does them (OS events, src/os/events.ts) and kept in this browser
 * only. Each completion sends a phone notification; the log itself is an
 * in-phone app. The arrival notifications point at the résumé and the log.
 */

import { emit, emitCabinetPlay, onOs, type OsEvent } from './events';
import { notify } from './notify';
import { getPhone } from './phone';

interface Mission {
  id: string;
  title: string;
  hint: string;
  done: (e: OsEvent) => boolean;
}

const MISSIONS: Mission[] = [
  { id: 'file', title: 'Open the subject file', hint: 'Experience on the phone, or the Empire State tag', done: e => e.type === 'app' && e.id === 'dossier' || e.type === 'room' && e.id === 'experience' },
  { id: 'resume', title: 'Retrieve Resume.pdf', hint: 'Top of the phone, one tap', done: e => e.type === 'file' && e.id === 'resume' },
  { id: 'breach', title: 'Breach a landmark', hint: 'Select a tag on the city map', done: e => e.type === 'room' },
  { id: 'record', title: 'Recover a project record', hint: 'Projects, then any record', done: e => e.type === 'record' },
  { id: 'terminal', title: 'Run a terminal command', hint: 'Terminal on the phone; try ls projects', done: e => e.type === 'terminal' },
  { id: 'scan', title: 'Profile your own device', hint: 'Profiler on the phone', done: e => e.type === 'scan' },
  { id: 'play', title: 'Play an arcade cabinet', hint: 'Arcade at One Times Square', done: e => e.type === 'play' },
  { id: 'comms', title: 'Open the comms channel', hint: 'Contact, or the Brooklyn Bridge booth', done: e => e.type === 'app' && e.id === 'contact' || e.type === 'room' && e.id === 'contact' },
];

const KEY = 'xw-missions';

function load(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(KEY) ?? '[]') as string[]); }
  catch { return new Set(); }
}

function save(done: Set<string>): void {
  try { localStorage.setItem(KEY, JSON.stringify([...done])); }
  catch { /* progress is a convenience */ }
}

const done = load();
const listeners = new Set<() => void>();

function updateBadge(): void {
  document.querySelectorAll<HTMLElement>('[data-xw-mission-count]').forEach(el => {
    el.textContent = `${done.size}/${MISSIONS.length}`;
  });
}

export function openMissionLog(): void {
  getPhone()?.openScreen('missions', 'Mission log', body => {
    const list = document.createElement('ol');
    list.className = 'xw-missions';
    const render = () => {
      list.replaceChildren(...MISSIONS.map(m => {
        const li = document.createElement('li');
        const complete = done.has(m.id);
        li.className = `xw-mission${complete ? ' xw-mission--done' : ''}`;
        li.innerHTML = '<span class="xw-mission-state"></span><span class="xw-mission-text"><span class="xw-mission-title"></span><span class="xw-mission-hint"></span></span>';
        li.querySelector('.xw-mission-state')!.textContent = complete ? 'Done' : 'Open';
        li.querySelector('.xw-mission-title')!.textContent = m.title;
        li.querySelector('.xw-mission-hint')!.textContent = m.hint;
        return li;
      }));
    };
    const summary = document.createElement('p');
    summary.className = 'xw-missions-summary';
    const sync = () => {
      render();
      summary.textContent = `${done.size} of ${MISSIONS.length} complete. Progress stays in this browser.`;
    };
    sync();
    body.append(summary, list);
    listeners.add(sync);
    return () => listeners.delete(sync);
  });
}

export function initMissions(): void {
  updateBadge();
  document.querySelectorAll('[data-xw-phone="missions"]').forEach(el => el.addEventListener('click', openMissionLog));

  // Résumé links anywhere (the phone file, room terminals, documents) count.
  document.addEventListener('click', e => {
    const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href]');
    if (!a) return;
    const path = new URL(a.href, location.origin).pathname;
    if (/^\/resume\/(pdf|download)$/.test(path)) emit({ type: 'file', id: 'resume' });
    // Phones open cabinets as plain links to the hosted games.
    if (document.body.classList.contains('xw-os-mobile')) emitCabinetPlay(a.href);
  }, true);

  onOs(e => {
    const hits = MISSIONS.filter(m => !done.has(m.id) && m.done(e));
    if (!hits.length) return;
    hits.forEach(m => done.add(m.id));
    // One card per event, naming every objective it completed.
    save(done);
    updateBadge();
    listeners.forEach(fn => fn());
    notify({
      app: 'Mission log',
      title: `Objective complete: ${hits.map(h => h.title).join('; ')}`,
      body: done.size === MISSIONS.length ? 'Every objective done. Thanks for looking around.' : `${done.size} of ${MISSIONS.length} complete.`,
      action: openMissionLog,
      actionLabel: 'Open log',
    });
  });

  // First visit this session: the file is in the phone; the log is there when wanted.
  try {
    if (sessionStorage.getItem('xw-greeted')) return;
    sessionStorage.setItem('xw-greeted', '1');
  } catch { return; }
  const greet = () => window.setTimeout(() => {
    notify({ app: 'Files', title: 'Resume.pdf received', body: 'One page. Open it from the top of your phone.' });
    // Only when nothing is done yet; a completion card already opens the log.
    if (!done.size) window.setTimeout(() => notify({
      app: 'Mission log',
      title: `${MISSIONS.length - done.size} objectives open`,
      body: 'Optional. Open the log to see the list.',
      action: openMissionLog,
      actionLabel: 'Open log',
    }), 1400);
  }, 900);
  if (document.body.classList.contains('xw-boot-done')) greet();
  else window.addEventListener('xw:intro-ready', greet, { once: true });
}
